import { $fetch } from 'utils/fetch'

const BASE_PATH = '/v1/knowledge'

/**
 * Create a new suggestion for a knowledge base
 * @param {string} knowledgeBaseId - Knowledge base ID
 * @param {object} data - Suggestion data
 * @param {string} data.question - The question
 * @param {string} data.answer - The answer
 * @param {string} data.type - Type: 'SUGGESTION' or 'FEEDBACK'
 * @returns {Promise<object>} Created suggestion
 */
export async function createSuggestion(knowledgeBaseId, data) {
  const response = await $fetch(`${BASE_PATH}/${knowledgeBaseId}/improve`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
  return response.suggestion
}

/**
 * List suggestions for a knowledge base
 * @param {string} knowledgeBaseId - Knowledge base ID
 * @param {object} params - Query parameters
 * @param {string} params.type - Filter by type: 'SUGGESTION', 'FEEDBACK', or 'ALL'
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.limit - Items per page (default: 20)
 * @returns {Promise<object>} Suggestions list with pagination
 */
export async function getSuggestions(knowledgeBaseId, { type, page = 1, limit = 20 } = {}) {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  })

  if (type && type !== 'ALL') {
    params.append('type', type)
  }

  return $fetch(`${BASE_PATH}/${knowledgeBaseId}/suggestions?${params}`)
}

/**
 * Get a specific suggestion by ID
 * @param {string} suggestionId - Suggestion ID
 * @returns {Promise<object>} Suggestion details
 */
export async function getSuggestion(suggestionId) {
  return $fetch(`${BASE_PATH}/suggestions/${suggestionId}`)
}

/**
 * Update a suggestion
 * @param {string} suggestionId - Suggestion ID
 * @param {object} data - Updated data
 * @param {string} data.question - Updated question
 * @param {string} data.answer - Updated answer
 * @returns {Promise<object>} Updated suggestion
 */
export async function updateSuggestion(suggestionId, data) {
  const response = await $fetch(`${BASE_PATH}/suggestions/${suggestionId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
  return response.suggestion
}

/**
 * Delete a suggestion
 * @param {string} suggestionId - Suggestion ID
 * @returns {Promise<object>} Deletion result
 */
export async function deleteSuggestion(suggestionId) {
  return $fetch(`${BASE_PATH}/suggestions/${suggestionId}`, {
    method: 'DELETE',
  })
}
